import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getAvatarUrl } from '@/lib/avatars';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface UserSuggestion {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  rows?: number;
}

export interface MentionInputRef {
  focus: () => void;
  scrollIntoView: (options?: ScrollIntoViewOptions) => void;
}

export const MentionInput = forwardRef<MentionInputRef, MentionInputProps>(
  ({ value, onChange, onKeyDown, onFocus, onBlur, placeholder, disabled, className, rows = 1 }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionStartPos, setMentionStartPos] = useState<number | null>(null);
    const [isSearching, setIsSearching] = useState(false);

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      scrollIntoView: (options) => textareaRef.current?.scrollIntoView(options),
    }));

    // Search for users when mention query changes
    useEffect(() => {
      if (!mentionQuery || mentionQuery.length < 1) {
        setSuggestions([]);
        return;
      }

      const searchUsers = async () => {
        setIsSearching(true);
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('user_id, display_name, avatar_url')
            .ilike('display_name', `%${mentionQuery}%`)
            .limit(5);

          if (error) throw error;
          setSuggestions(data || []);
        } catch (error) {
          console.error('Error searching users:', error);
          setSuggestions([]);
        } finally {
          setIsSearching(false);
        }
      };

      const debounce = setTimeout(searchUsers, 200);
      return () => clearTimeout(debounce);
    }, [mentionQuery]);

    // Detect @ mentions while typing
    const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart || 0;
      
      onChange(newValue);

      // Look backwards from cursor to find @ symbol
      const textBeforeCursor = newValue.slice(0, cursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');
      
      if (lastAtIndex !== -1) {
        // Check if @ is at start or preceded by whitespace
        const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
        if (charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) {
          const query = textBeforeCursor.slice(lastAtIndex + 1);
          // Only show suggestions if query doesn't contain spaces (still typing username)
          if (!query.includes(' ') && query.length <= 30) {
            setMentionQuery(query);
            setMentionStartPos(lastAtIndex);
            setShowSuggestions(true);
            setSelectedIndex(0);
            return;
          }
        }
      }
      
      // No valid mention being typed
      setShowSuggestions(false);
      setMentionQuery('');
      setMentionStartPos(null);
    }, [onChange]);

    // Handle keyboard navigation in suggestions
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (showSuggestions && suggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % suggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          selectSuggestion(suggestions[selectedIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setShowSuggestions(false);
          return;
        }
      }
      
      // Pass through to parent handler
      onKeyDown?.(e);
    }, [showSuggestions, suggestions, selectedIndex, onKeyDown]);

    // Insert selected mention
    const selectSuggestion = useCallback((user: UserSuggestion) => {
      if (mentionStartPos === null || !textareaRef.current) return;

      const displayName = user.display_name || 'Anonymous';
      // Replace spaces with underscores for the mention format
      const mentionText = `@${displayName.replace(/\s+/g, '')} `;
      
      const before = value.slice(0, mentionStartPos);
      const cursorPos = textareaRef.current.selectionStart || value.length;
      const after = value.slice(cursorPos);
      
      const newValue = before + mentionText + after;
      onChange(newValue);
      
      // Reset mention state
      setShowSuggestions(false);
      setMentionQuery('');
      setMentionStartPos(null);
      
      // Set cursor position after mention
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = mentionStartPos + mentionText.length;
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 0);
    }, [mentionStartPos, value, onChange]);

    // Close suggestions when clicking outside
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          dropdownRef.current && 
          !dropdownRef.current.contains(e.target as Node) &&
          !textareaRef.current?.contains(e.target as Node)
        ) {
          setShowSuggestions(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div className="relative flex-1">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("min-h-[44px] max-h-[120px] resize-none py-3 flex items-center", className)}
          rows={rows}
        />
        
        {/* Mention suggestions dropdown */}
        {showSuggestions && (suggestions.length > 0 || isSearching) && (
          <div
            ref={dropdownRef}
            className="absolute bottom-full left-0 right-0 mb-1 bg-popover border rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto"
          >
            {isSearching && suggestions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Searching...
              </div>
            ) : suggestions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No users found
              </div>
            ) : (
              suggestions.map((user, index) => (
                <button
                  key={user.user_id}
                  type="button"
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent transition-colors",
                    index === selectedIndex && "bg-accent"
                  )}
                  onClick={() => selectSuggestion(user)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={getAvatarUrl(user.avatar_url, user.user_id || user.display_name || 'unknown')} />
                    <AvatarFallback className="bg-muted" />
                  </Avatar>
                  <span className="text-sm font-medium truncate">
                    {user.display_name || 'Anonymous'}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    );
  }
);

MentionInput.displayName = 'MentionInput';
